
import { computed, defineComponent, ref, onBeforeUpdate } from "vue";
import { useRouter } from "vue-router";
import { routeNames } from "@/router/index";
import store from "@/stores";
import {
  IQuestionnaireData,
  SelectedQuestionnaires,
  consts,
} from "@/services/Models";
import { useLogger } from "@/lib/logger";

//シナリオ属性グループを判断する番号
const senarioNo = consts.senarioAttrGroupID;
//シーン属性グループを判断する番号
const sceneNo = consts.sceneAttrGroupID;
//タッチ属性グループを判断する番号
const touchNo = consts.touchAttrGroupID;

// MEMO アンケートは現状1つの属性グループ（シナリオ）の属性を決定する仕様になっている。
// これを動的／汎用的に行えるデータ構造を設計してもアンケートの用途から全く違う仕様変更が発生する可能性が高いと考える
// そのためアンケートデータは現時点でDBには持たない。
const questionnaires: IQuestionnaireData[] = [
  {
    title: "Question1",
    message: "Q1. あなたの商品/サービスの浸透具合は？",
    choices: [
      {
        text: "ひろい",
        choiceID: 0,
      },
      {
        text: "まだまだ",
        choiceID: 1,
      },
    ],
  },
  {
    title: "Question2",
    message: "Q2. ターゲット(広告を見る人)の悩みの状態は？",
    choices: [
      {
        text: "ハッキリしていそう",
        choiceID: 0,
      },
      {
        text: "あいまいかも",
        choiceID: 1,
      },
    ],
  },
  {
    title: "Question3",
    message: "Q3. あなたの広告のどこに重点をおきたい？",
    choices: [
      {
        text: "インパクト",
        choiceID: 0,
      },
      {
        text: "商品理解",
        choiceID: 1,
      },
    ],
  },
];

export default defineComponent({
  name: "Questionnaire",
  props: {
    isShowModal: { type: Boolean },
  },
  setup(props, context) {
    const resource = store.useResource();
    const router = useRouter();
    const logger = useLogger();

    const isShowModalInner = computed({
      get: () => props.isShowModal,
      set: (newValue) => {
        context.emit("update:isShowModal", newValue);
      },
    });
    const selectedList = ref<Map<IQuestionnaireData, number>>(new Map());
    const select = (q: IQuestionnaireData, choiceID: number) => {
      selectedList.value.set(q, choiceID);
    };
    const isSelected = computed(
      () => selectedList.value.size != questionnaires.length
    );
    // アンケート結果に基づくテンプレート検索処理
    const toSearch = () => {
      const choiceIDs = Array.from(selectedList.value).map(
        ([_, choiceID]) => choiceID
      );

      const choices = Array.from(selectedList.value).map(([q, x]) => {
        return {
          message: q.message,
          selectedText: q.choices[x].text,
        };
      });

      // ログ出力情報
      const sendLogInfo: SelectedQuestionnaires = {
        action: routeNames.SearchTemplate,
        options: {
          choices: choices,
        },
      };

      //ユーザーログ書き込み
      logger.log(sendLogInfo);

      //属性グループごとの先頭の属性IDを格納
      const firstAttrIDs = resource.data.attributeGroups.map((g) => ({
        gID: g.id,
        firstAttrID: g.attributes.find((_) => true)?.id,
      }));

      //シナリオ属性グループの先頭の属性IDを取得
      var senarioAttributeGroupsLeadID = firstAttrIDs.find(
        (x) => x.gID == senarioNo
      )?.firstAttrID;
      if (senarioAttributeGroupsLeadID == null) return;

      //各属性グループの先頭のIDを配列に格納(シーン：ビジネス、タッチ：男性マンガを指定)
      const initSelectIdsNotInSenario = firstAttrIDs
        .filter(
          (x) => x.gID != senarioNo && (x.gID == sceneNo || x.gID == touchNo)
        )
        .map((x) => x.firstAttrID);

      //初期選択値のシナリオ部分
      if (choiceIDs[0] === 0) {
        if (choiceIDs[1] === 0) {
          if (choiceIDs[2] === 0) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 3);
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 2);
          } else if (choiceIDs[2] === 1) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 4);
          }
        } else if (choiceIDs[1] === 1) {
          if (choiceIDs[2] === 0) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 3);
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 2);
          } else if (choiceIDs[2] === 1) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID);
          }
        }
      } else if (choiceIDs[0] === 1) {
        if (choiceIDs[1] === 0) {
          if (choiceIDs[2] === 0) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 3);
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 2);
          } else if (choiceIDs[2] === 1) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 1);
          }
        } else if (choiceIDs[1] === 1) {
          if (choiceIDs[2] === 0) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 3);
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 2);
          } else if (choiceIDs[2] === 1) {
            initSelectIdsNotInSenario.push(senarioAttributeGroupsLeadID + 4);
          }
        }
      }

      router.push({
        name: routeNames.SearchTemplate,
        query: {
          attributeID: initSelectIdsNotInSenario,
          hideAttributeGroupID: senarioNo,
          fromQ: "1",
        },
        force: true,
      });
      context.emit("update:isShowModal", false);
    };
    onBeforeUpdate(() => {
      selectedList.value = new Map();
    });

    // エンター押下時の動作
    const enterAction = () => {
      if (!isSelected.value) toSearch();
    };

    return {
      questionnaires,
      isShowModalInner,
      isSelected,
      toSearch,
      select,
      enterAction,
    };
  },
});
